import { requestHeaders } from '@/api';
import type { PatientDetails } from '@/components/MergePatients/MergePatientsModalContent';
import { emptyPatientDetails } from '@/components/MergePatients/MergePatientsModalContent';
import settings from '@/data/constants';
import { formatAddress } from '@/utils/formatter';
import { Checkbox, TextField, Typography } from '@montugroup/design-system';
import { InputAdornment, styled } from '@mui/material';
import Box from '@mui/material/Box';
import { format } from 'date-fns';
import { useState } from 'react';

export enum MergeProfileInputType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

type GetPatientDetailByAlpatResponse = {
  patient_code: string;
  gender: string;
  medicare_number: string;
  medicare_ref_number: string;
  medicare_exp_date: string;
  dob: string;
  dva_number: string;
  PatientUser: {
    first_name: string;
    last_name: string;
    email: string;
    address: string;
    zip_code: string;
    city: string;
  };
};

type MergeProfileInputProps = {
  type: MergeProfileInputType;
  patientDetails: PatientDetails;
  setPatientDetails: (patientDetails: PatientDetails) => void;
};

type StyledTextFieldWrapperProps = {
  type: MergeProfileInputType;
};

const StyledTextFieldWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'type',
})<StyledTextFieldWrapperProps>(({ theme, type }) => ({
  border: `${theme.spacing(1)} solid ${type === MergeProfileInputType.PRIMARY ? '#CEEDFF' : '#EBEAEE'}`,
  borderRadius: theme.spacing(2),
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

const StyledPatientDetailContainer = styled(Box)({});

const StyledPatientDetailLabel = styled(Typography)(() => ({
  fontWeight: 'lighter',
}));

const StyledPatientDetailValue = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

export const MergeProfileInput = ({ type, patientDetails, setPatientDetails }: MergeProfileInputProps) => {
  const [patientCodeInput, setPatientCodeInput] = useState('');
  const [checkBoxEnabled, setCheckBoxEnabled] = useState(false);
  const [checkBoxHidden, setCheckBoxHidden] = useState(false);
  const [patientNotFound, setPatientNotFound] = useState(false);

  const onChange = (e: { target: { value: string } }) => {
    const value = e.target.value.replace(/\s/g, '');
    setPatientCodeInput(value.toUpperCase());
    setPatientNotFound(false);
    setPatientDetails(emptyPatientDetails);
    if (value.length > 6) {
      setCheckBoxHidden(false);
      setCheckBoxEnabled(true);
    } else {
      setCheckBoxEnabled(false);
    }
  };

  const fetchPatientDetails = async () => {
    try {
      setCheckBoxHidden(true);

      const url = `${settings.url}/patient/detail/${patientCodeInput}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: requestHeaders(),
      });

      if (!response.ok) {
        throw new Error(`${response.status}`);
      }

      const responseData: GetPatientDetailByAlpatResponse = await response.json();

      if (responseData) {
        const mappedResponse = {
          patientCode: responseData.patient_code ?? '',
          firstName: responseData.PatientUser.first_name ?? '',
          lastName: responseData.PatientUser.last_name ?? '',
          email: responseData.PatientUser.email,
          dob: responseData.dob ? format(new Date(responseData.dob), 'dd-MMM-yyyy') : '',
          gender: responseData.gender ?? '',
          medicareNumber: responseData.medicare_number ?? '',
          dvaNumber: responseData.dva_number ?? '',
          address: formatAddress({
            address: responseData.PatientUser.address,
            city: responseData.PatientUser.city,
            postCode: responseData.PatientUser.zip_code,
          }),
        };
        setPatientDetails(mappedResponse);
        setPatientNotFound(false);
      } else {
        setPatientNotFound(true);
        setPatientDetails(emptyPatientDetails);
      }
    } catch (error) {
      console.error('Error fetching patient details: ', error);
      setPatientNotFound(true);
      setPatientDetails(emptyPatientDetails);
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight="light" marginBottom={2}>
          {type === MergeProfileInputType.PRIMARY ? 'Primary profile' : 'Secondary profile'}
        </Typography>
        {type === MergeProfileInputType.SECONDARY && (
          <Typography variant="subtitle2" fontWeight="light" marginBottom={2} color="error" fontStyle="italic">
            Will be removed
          </Typography>
        )}
      </Box>
      <StyledTextFieldWrapper component={'form'} type={type}>
        <StyledTextField
          label={'ALPAT'}
          variant={'outlined'}
          fullWidth
          onChange={onChange}
          InputProps={{
            endAdornment: !checkBoxHidden && (
              <InputAdornment position="end">
                <Checkbox disabled={!checkBoxEnabled} onClick={fetchPatientDetails} />
              </InputAdornment>
            ),
          }}
          error={patientNotFound}
          helperText={patientNotFound ? 'Profile not found' : ''}
        />
        {patientDetails.firstName && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>First name</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.firstName}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.lastName && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>Last name</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.lastName}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.dob && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>Date of Birth</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.dob}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.gender && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>Gender</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.gender}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.medicareNumber && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>Medicare number</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.medicareNumber}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.dvaNumber && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>DVA number</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.dvaNumber}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
        {patientDetails.address && (
          <StyledPatientDetailContainer>
            <StyledPatientDetailLabel variant={'subtitle2'}>Address</StyledPatientDetailLabel>
            <StyledPatientDetailValue>{patientDetails.address}</StyledPatientDetailValue>
          </StyledPatientDetailContainer>
        )}
      </StyledTextFieldWrapper>
    </Box>
  );
};
